import { Navigate, useLocation } from 'react-router-dom';
import { usePlanLimitations } from '@/hooks/usePlanLimitations';
import { ReactNode } from 'react';

interface FeatureProtectedRouteProps {
  /**
   * The name of the feature to check access for
   */
  featureName: string;
  /**
   * The path to redirect to if the feature is not available
   */
  redirectTo?: string;
  /**
   * The component to render if the feature is available
   */
  children: ReactNode;
}

/**
 * A route component that redirects to a "feature not available" page
 * if the user doesn't have access to the feature based on their plan.
 */
export function FeatureProtectedRoute({ 
  featureName, 
  redirectTo = `/plan-upgrade/${featureName}`,
  children 
}: FeatureProtectedRouteProps) {
  const { isFeatureAvailable, loading } = usePlanLimitations();
  const location = useLocation();

  if (loading) {
    // Show a loading state while checking plan limitations
    return <div className="flex items-center justify-center min-h-screen w-full">Loading...</div>;
  }

  if (!isFeatureAvailable(featureName)) {
    // Redirect to the upgrade page with the current location stored
    // so we can return after upgrading
    return (
      <Navigate 
        to={redirectTo} 
        state={{ from: location.pathname }} 
        replace 
      />
    );
  }

  return <>{children}</>;
}